import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield, Loader2, AlertCircle, Mail, Check, ArrowLeft } from 'lucide-react'
import { forgotPassword } from '../api/client'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!email.trim()) { setError('Enter your email.'); return }
    setSubmitting(true)
    try {
      await forgotPassword(email.trim())
      setSubmitted(true)
    } catch (err) {
      // Backend never leaks whether the email exists — any error here is unexpected.
      setError((err as { message?: string })?.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mb-4 text-xs font-medium text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 transition-colors">
          <ArrowLeft size={13} /> Back to home
        </Link>
        <div className="card p-8 md:p-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
              <Shield size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                Forgot your password?
              </h1>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Enter your email and we'll send you a reset link.
              </p>
            </div>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-sm">
                <Check size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Check your inbox.</p>
                  <p className="mt-0.5 text-[13px] text-emerald-700/80 dark:text-emerald-400/80">
                    If an account exists for that email, a password reset link is on its way.
                  </p>
                </div>
              </div>
              <Link to="/signin" className="btn-primary w-full justify-center">Back to sign in</Link>
            </div>
          ) : (
            <>
              {error && (
                <div role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="label flex items-center gap-1.5">
                    <Mail size={13} /> Email
                  </label>
                  <input id="email" type="email" autoComplete="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required className="input" />
                </div>

                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Send reset link
                </button>
              </form>

              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Remembered it?{' '}
                <Link to="/signin" className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-medium">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
